// Helpers puros de payment methods (sin dependencias de React/axios) para que
// sean testeables sin arrastrar services/api.js (axios ESM no transforma en jest).

// Defaults de shape plano por método (para render del form).
const METHOD_DEFAULTS = {
  cash: { enabled: true },
  card: { enabled: false },
  transfer: { enabled: false, bank: '', cbu: '', alias: '' },
  mercadoPago: { enabled: false, publicKey: '', accessToken: '' },
  lineCheckout: { enabled: false, publicKey: '', secretKey: '' },
};

// Métodos que renderiza el form, en orden.
export const PAYMENT_METHOD_KEYS = ['cash', 'card', 'transfer', 'mercadoPago', 'lineCheckout'];

// Campos por método que PUEDEN heredarse de los defaults org-level. Espejo del
// backend (services/restaurants/shared/paymentMethods.js INHERITABLE_FIELDS):
// flags `enabled` + datos bancarios no sensibles del transfer. Las credenciales
// técnicas (accessToken/publicKey/secretKey) NUNCA heredan — siempre son
// per-restaurant y sólo pueden venir del override de la sucursal.
export const INHERITABLE_FIELDS = {
  cash: ['enabled'],
  card: ['enabled'],
  transfer: ['enabled', 'bank', 'cbu', 'alias'],
  mercadoPago: ['enabled'],
  lineCheckout: ['enabled'],
};

// Migra la key legacy minúscula mercadopago → mercadoPago.
const normalizeMpKey = (bag) => {
  if (!bag || typeof bag !== 'object' || Array.isArray(bag)) return {};
  const out = { ...bag };
  if (out.mercadopago && !out.mercadoPago) out.mercadoPago = out.mercadopago;
  delete out.mercadopago;
  return out;
};

// Normaliza el shape de payment methods al contrato que lee el backend de pagos.
// Clave: el backend lee `mercadoPago` (camelCase) top-level; aceptamos legacy
// `mercadopago` (minúscula) de datos viejos pero siempre emitimos `mercadoPago`.
export const normalizePaymentMethods = (paymentMethods) => ({
  cash: paymentMethods?.cash || { enabled: true },
  card: paymentMethods?.card || { enabled: false },
  mercadoPago: paymentMethods?.mercadoPago ||
    paymentMethods?.mercadopago || { enabled: false, publicKey: '', accessToken: '' },
  transfer: paymentMethods?.transfer || { enabled: false, cbu: '', alias: '', bank: '' },
  lineCheckout: paymentMethods?.lineCheckout || { enabled: false, publicKey: '', secretKey: '' },
});

// Normaliza un valor `paymentMethods` (legacy plano o nuevo `{inherit,overrides}`)
// a la forma `{ inherit, overrides }`. Espejo del normalizePaymentMethods del
// backend para que el front emita exactamente el shape que el backend valida.
export const toInheritShape = (paymentMethods) => {
  if (!paymentMethods || typeof paymentMethods !== 'object' || Array.isArray(paymentMethods)) {
    return { inherit: false, overrides: {} };
  }
  const hasInheritShape =
    typeof paymentMethods.inherit === 'boolean' ||
    (paymentMethods.overrides && typeof paymentMethods.overrides === 'object');
  if (hasInheritShape) {
    return {
      inherit: paymentMethods.inherit === true,
      overrides: normalizeMpKey(
        paymentMethods.overrides && typeof paymentMethods.overrides === 'object'
          ? paymentMethods.overrides
          : {}
      ),
    };
  }
  // Shape legacy plano → overrides per-restaurant sin herencia.
  return { inherit: false, overrides: normalizeMpKey(paymentMethods) };
};

// True si la org tiene defaults de métodos de pago configurados (al menos un
// método). Sin defaults NO se debe habilitar el toggle inherit: heredar de
// vacío desactivaría silenciosamente los métodos de pago de la sucursal.
export const hasOrgDefaults = (orgDefaults) =>
  !!orgDefaults &&
  typeof orgDefaults === 'object' &&
  !Array.isArray(orgDefaults) &&
  PAYMENT_METHOD_KEYS.some((m) => orgDefaults[m] && typeof orgDefaults[m] === 'object');

// Config editable de un método overrideado (defaults base + override del
// restaurante). Conserva las credenciales existentes para round-trip.
export const overrideMethodConfig = (overrides, method) => ({
  ...METHOD_DEFAULTS[method],
  ...(overrides?.[method] || {}),
});

// Config de SÓLO LECTURA que vería la sucursal cuando hereda: defaults org-level
// acotados a los campos heredables (sin credenciales) sobre los defaults base.
// Sirve tanto para los placeholders grisados como para sembrar un override nuevo.
export const inheritedMethodConfig = (orgDefaults, method) => {
  const def = normalizeMpKey(orgDefaults || {})[method] || {};
  const out = { ...METHOD_DEFAULTS[method] };
  for (const field of INHERITABLE_FIELDS[method]) {
    if (def[field] !== undefined) out[field] = def[field];
  }
  return out;
};
